import { TemplateFormat } from "@/components/Playground2/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Metadata } from "@/types";
import { CogIcon } from "lucide-react";
import FormatSelector from "./FormatSelector";
import MetadataSection from "./MetadataSection";
import ModeSwitch from "./ModeSwitch";
import SettingsSection from "./SettingsSection";

interface MessagesSettingsProps {
  readonly?: boolean;
  formats: string[];
  currentFormat: string;
  onFormatChange?: (format: TemplateFormat) => void;
  isCompletion: boolean;
  onModeChange?: () => void;
  tooltipContent?: string;
  dialogTitle?: string;
  dialogDescription?: string;
  formatSectionTitle?: string;
  modeSectionTitle?: string;
  metadata?: Metadata;
  setMetadata?: (metadata: Metadata) => void;
}

const MessagesSettings = ({
  readonly = false,
  formats,
  currentFormat,
  onFormatChange,
  isCompletion,
  onModeChange,
  tooltipContent = "Prompt Settings",
  dialogTitle = "Settings",
  dialogDescription = "Customize your template preferences",
  formatSectionTitle = "Message Format",
  modeSectionTitle = "Display Mode",
  metadata = {},
  setMetadata,
}: MessagesSettingsProps) => {
  return (
    <Dialog>
      <BasicTooltip content={tooltipContent}>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full transition-all duration-200 hover:bg-gray-100 active:scale-95"
          >
            <CogIcon className="h-5 w-5 text-gray-500 transition-colors hover:text-gray-700" />
          </Button>
        </DialogTrigger>
      </BasicTooltip>
      <DialogContent className="w-full max-w-md overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {dialogTitle}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            {dialogDescription}
          </DialogDescription>
        </DialogHeader>
        <div className="w-full space-y-6 overflow-y-auto pr-1">
          <SettingsSection
            title={formatSectionTitle}
            info="Modify template variable parsing behavior"
          >
            <FormatSelector
              formats={formats}
              currentFormat={currentFormat}
              onChange={onFormatChange}
              readonly={readonly}
            />
          </SettingsSection>

          <SettingsSection
            title={modeSectionTitle}
            info="Change the conversation mode"
          >
            <div className="flex justify-center">
              <ModeSwitch
                isCompletion={isCompletion}
                onChange={onModeChange}
                readonly={readonly}
              />
            </div>
          </SettingsSection>

          <SettingsSection
            title="Metadata"
            info="Use metadata to store additional information with your prompt template"
          >
            <MetadataSection
              metadata={metadata}
              setMetadata={readonly ? undefined : setMetadata}
              readonly={readonly}
            />
          </SettingsSection>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MessagesSettings;
